import { queryOptional } from "../utils/dom";

// Event Delegation for Test Runner
export function initTestRunner(): () => void {
  const clickHandler = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const card = target.closest<HTMLElement>(".answer-card");

    if (card && document.querySelector("#question_frame")) {
      handleCardClick(card);
    }
  };

  const keydownHandler = (e: KeyboardEvent) => {
    handleKeydown(e);
  };

  const submitHandler = (e: Event) => {
    const form = e.target as HTMLFormElement;
    if (form && form.dataset.controller === "test-runner") {
      injectTimeMs(form);
    }
  };

  document.addEventListener("click", clickHandler);
  document.addEventListener("keydown", keydownHandler);
  document.addEventListener("submit", submitHandler, true);
  startTimer();
  resetQuestionStartTime();
  document.body.dataset.testRunnerReady = "true";

  return () => {
    document.removeEventListener("click", clickHandler);
    document.removeEventListener("keydown", keydownHandler);
    document.removeEventListener("submit", submitHandler, true);
    if (timerInterval) {
      clearInterval(timerInterval);
      timerInterval = null;
    }
    delete document.body.dataset.testRunnerReady;
  };
}

// Auto-initialize
document.addEventListener("turbo:load", () => {
  if (document.querySelector("[data-controller='test-runner']")) {
    initTestRunner();
    resetQuestionStartTime();
  }
});

document.addEventListener("turbo:before-cache", () => {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  questionStartTime = null;
});

function resetQuestionStartTime() {
  questionStartTime = Date.now();
}

function getQuestionElapsedTimeMs(): number {
  if (!questionStartTime) return 0;
  return Date.now() - questionStartTime;
}

function injectTimeMs(form: HTMLFormElement) {
  let timeInput = queryOptional<HTMLInputElement>(
    "input[name='time_ms']",
    form,
  );
  if (!timeInput) {
    timeInput = document.createElement("input");
    timeInput.type = "hidden";
    timeInput.name = "time_ms";
    form.appendChild(timeInput);
  }
  timeInput.value = getQuestionElapsedTimeMs().toString();
}

function handleCardClick(card: HTMLElement) {
  const container = card.closest<HTMLElement>("#question_frame");
  if (!container) return;

  const input = queryOptional<HTMLInputElement>(
    "input[name='answer_option_id']",
    container,
  );
  const submitBtn = queryOptional<HTMLButtonElement>(
    "button[type='submit']",
    container,
  );
  const cards = Array.from(
    container.querySelectorAll<HTMLElement>(".answer-card"),
  );

  // 1. Visual Selection
  cards.forEach((c) => {
    c.classList.remove("selected", "border-blue-500", "bg-blue-500/10");
    c.classList.add("border-white/10");

    // Reset Icon
    const icon = c.querySelector(".status-icon");
    if (icon) {
      icon.className =
        "w-8 h-8 rounded-full border-2 border-gray-500 group-hover:border-blue-400 flex items-center justify-center text-sm font-bold text-gray-400 group-hover:text-blue-400 transition status-icon";
    }
  });

  card.classList.add("selected", "border-blue-500", "bg-blue-500/10");
  card.classList.remove("border-white/10");

  // Set Active Icon
  const icon = card.querySelector(".status-icon");
  if (icon) {
    icon.className =
      "w-8 h-8 rounded-full border-2 border-blue-500 bg-blue-500 flex items-center justify-center text-sm font-bold text-white shadow-lg status-icon";
  }

  // 2. Update Input
  const id = card.getAttribute("data-id");
  if (id && input) {
    input.value = id;
  }

  // 3. Enable Submit
  if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.classList.remove("opacity-50", "cursor-not-allowed");
  }
}

function handleKeydown(e: KeyboardEvent) {
  const container = document.querySelector<HTMLElement>("#question_frame");
  if (!container) return;

  // Skip if user is typing in a text input
  const activeElement = document.activeElement;
  if (
    activeElement?.tagName === "INPUT" ||
    activeElement?.tagName === "TEXTAREA"
  ) {
    return;
  }

  const cards = Array.from(
    container.querySelectorAll(".answer-card"),
  ) as HTMLElement[];

  // Answer selection: bind BOTH 1-4 and A-D
  const keyMap: Record<string, number> = {
    "1": 0,
    a: 0,
    A: 0,
    "2": 1,
    b: 1,
    B: 1,
    "3": 2,
    c: 2,
    C: 2,
    "4": 3,
    d: 3,
    D: 3,
  };

  if (e.key in keyMap) {
    e.preventDefault();
    const index = keyMap[e.key];
    if (index !== undefined) {
      const card = cards[index];
      if (card) handleCardClick(card);
    }
    return;
  }

  // Submit: Enter (explicit, not auto)
  if (e.key === "Enter") {
    const submitBtn = container.querySelector(
      "button[type='submit']",
    ) as HTMLButtonElement;
    if (submitBtn && !submitBtn.disabled) {
      e.preventDefault();
      const form = container.querySelector("form") as HTMLFormElement;
      if (form && !form.dataset.submitting) {
        form.dataset.submitting = "true";
        form.requestSubmit();
      }
    }
  }
}

// Timer Logic
let timerInterval: number | null = null;
let questionStartTime: number | null = null;

function updateTimerDisplay(): boolean {
  const timerElement = document.querySelector(
    "[data-controller='countdown']",
  ) as HTMLElement;

  if (!timerElement) return false;

  // Get fixed end timestamp from server (seconds since epoch)
  const endTime = parseFloat(timerElement.dataset.countdownEndTimeValue || "0");

  if (!endTime) return false;

  // Calculate remaining time from fixed end timestamp
  const now = Date.now() / 1000;
  const remainingSeconds = Math.max(0, Math.floor(endTime - now));

  if (remainingSeconds <= 0) {
    timerElement.textContent = "00:00";
    // Auto-submit
    const form = document.querySelector(
      "form[data-controller='test-runner']",
    ) as HTMLFormElement;
    if (form && !form.dataset.submitting) {
      form.dataset.submitting = "true";
      form.requestSubmit();
    }
    return true;
  }

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  // Warning state: red when under 60 seconds
  if (remainingSeconds <= 60) {
    timerElement.classList.remove("text-slate-300");
    timerElement.classList.add("text-red-400");
    const dot = document.getElementById("timer-dot");
    if (dot) {
      dot.classList.remove("bg-emerald-500");
      dot.classList.add("bg-red-500", "animate-pulse");
    }
  }

  return true;
}

function startTimer() {
  if (timerInterval) clearInterval(timerInterval);

  // Update immediately to prevent "--:--" blink
  updateTimerDisplay();

  timerInterval = window.setInterval(() => {
    updateTimerDisplay();
  }, 1000);
}
